import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Restore as RestoreIcon,
  DeleteForever as DeleteIcon,
} from '@mui/icons-material';
import { resolveEmployeeStatusMeta } from '../utils/employeeStatus';

interface Employee {
  id: string;
  code: string;
  name: string;
  email: string;
  jobTitle?: { name: string } | string;
  department?: { name: string } | string;
  company?: { name: string } | string;
  status: unknown;
  deleted_at: string;
  deleted_by_name: string;
}

const resolveNestedName = (value: { name?: string } | string | undefined): string => {
  if (!value) {
    return '-';
  }
  if (typeof value === 'string') {
    return value.trim() || '-';
  }
  return value.name?.trim() || '-';
};

interface DeletedEmployeesListProps {
  employees: Employee[];
  selectedEmployees: string[];
  onSelectEmployee: (employeeId: string) => void;
  onSelectAll: () => void;
  onRestore: (employeeId: string) => void;
  onPermanentDelete: (employeeId: string) => void;
  isLoading?: boolean;
}

const DeletedEmployeesList: React.FC<DeletedEmployeesListProps> = ({
  employees,
  selectedEmployees,
  onSelectEmployee,
  onSelectAll,
  onRestore,
  onPermanentDelete,
  isLoading = false,
}) => {
  const allSelected = employees.length > 0 && selectedEmployees.length === employees.length;
  const someSelected = selectedEmployees.length > 0 && selectedEmployees.length < employees.length;

  return (
    <TableContainer sx={{ maxHeight: 'calc(90vh - 200px)' }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onSelectAll}
                disabled={isLoading}
              />
            </TableCell>
            <TableCell>Mã nhân viên</TableCell>
            <TableCell>Tên nhân viên</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Chức vụ</TableCell>
            <TableCell>Phòng ban</TableCell>
            <TableCell>Công ty</TableCell>
            <TableCell>Trạng thái</TableCell>
            <TableCell>Ngày xóa</TableCell>
            <TableCell>Người xóa</TableCell>
            <TableCell align="center">Hành động</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {employees.map((employee) => {
            const statusMeta = resolveEmployeeStatusMeta(employee.status);

            return (
            <TableRow
              key={employee.id}
              hover
              sx={{
                backgroundColor: '#f9fafb',
                opacity: 0.7,
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedEmployees.includes(employee.id)}
                  onChange={() => onSelectEmployee(employee.id)}
                  disabled={isLoading}
                />
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    textDecoration: 'line-through',
                    color: '#6b7280',
                  }}
                >
                  {employee.code}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: 'line-through',
                    color: '#6b7280',
                  }}
                >
                  {employee.name}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: 'line-through',
                    color: '#6b7280',
                  }}
                >
                  {employee.email}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280',
                    textDecoration: 'line-through',
                  }}
                >
                  {resolveNestedName(employee.jobTitle)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280',
                    textDecoration: 'line-through',
                  }}
                >
                  {resolveNestedName(employee.department)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280',
                    textDecoration: 'line-through',
                  }}
                >
                  {resolveNestedName(employee.company)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={statusMeta.name}
                  size="small"
                  sx={{
                    backgroundColor: `${statusMeta.color}20`,
                    color: statusMeta.color,
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: '20px',
                    opacity: 0.7,
                  }}
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  {new Date(employee.deleted_at).toLocaleString('vi-VN')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  {employee.deleted_by_name || '-'}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Tooltip title="Khôi phục">
                    <IconButton
                      size="small"
                      onClick={() => onRestore(employee.id)}
                      disabled={isLoading}
                      sx={{ color: '#059669' }}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ display: 'inline-flex' }}>
                    <Tooltip title="Xóa vĩnh viễn">
                      <IconButton
                        size="small"
                        onClick={() => onPermanentDelete(employee.id)}
                        disabled={isLoading}
                        sx={{ 
                          color: '#ef4444',
                          display: 'inline-flex !important',
                          visibility: 'visible !important',
                          opacity: '1 !important',
                          '&:hover': {
                            backgroundColor: '#fef2f2',
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DeletedEmployeesList;
